import { equal, handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { Quiz } from '@/client/quizzes';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { BlockEmailsBanner } from '@/components/block-emails';
import { SurveyPreviewModal } from '@/components/security-culture/modals/SurveyPreviewModal';
import { Survey } from '@/components/security-culture/Survey';
import { SurveyModal } from '@/components/security-culture/SurveyModal';
import {
  useCloneQuiz,
  useDeleteQuiz,
  useQuizzes,
} from '@/hooks/query/quizzes.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { showBlockEmailsMessage } from '@/utils/helpers';
import { SCEditIcon, SurveyTemplateIcon } from '@/utils/helpers/icons.helper';
import { AxiosError } from 'axios';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { GroupCondition } from '../../api/enums/conditions.enum';
import { group } from '../../api/helpers/filter.helper';

const pathItems = (account: Account, t: TranslationFunctionType) => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('security.culture.survey'),
    url: RedirectPaths[RedirectPathsEnum.SURVEY](),
    template: AppBreadCrumbTemplate,
  },
];

const StyledContainer = styled(FlexContainer)`
  height: auto;
  width: 100%;
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  padding: var(--default-padding);

  p {
    text-align: center;
    line-height: var(--medium-line-height);
    font-weight: 500;
  }

  img {
    height: auto;
    width: 100%;
    margin-bottom: var(--default-padding);
  }

  .p-button {
    padding-inline: var(--big-padding);
    font-size: var(--large-font-size);
  }
`;

export const SurveyPage: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [surveyModal, setSurveyModal] = useState<boolean>(false);
  const [currentSurvey, setCurrentSurvey] = useState<Quiz | undefined>(
    undefined,
  );
  const [previewSurveyId, setPreviewSurveyId] = useState<string | undefined>(
    undefined,
  );
  const { quizzes, isLoading, refetch } = useQuizzes(
    currentAccount?.id as string,
    {
      filters: [
        group(GroupCondition.OR, [
          equal('account', currentAccount?.id),
          equal('account', null),
        ]),
      ],
    },
  );

  const cloneQuiz = useCloneQuiz();
  const handleCopyAndEditSurvey = async (surveyId: string) => {
    try {
      const response = await cloneQuiz.clone({
        id: surveyId,
        account: currentAccount?.id as string,
      });

      if (!response?.id) return;
      refetch();
      setCurrentSurvey(response);
      toast?.success(t('toast.success'), t('sc.survey.created'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setSurveyModal(true);
    }
  };

  const deleteQuiz = useDeleteQuiz();
  const handleDeleteSurvey = async (quizId: string) => {
    try {
      await deleteQuiz.delete({
        accountId: currentAccount?.id as string,
        quizId,
      });
      toast?.success(t('toast.success'), t('sc.survey.deleted'));
      refetch();
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const hasBlockEmailsBanner = showBlockEmailsMessage(currentAccount);

  return (
    <FlexContainer direction="column" align="flex-start" height="100%">
      {hasBlockEmailsBanner && <BlockEmailsBanner />}
      <AppBreadCrumb model={pathItems(currentAccount as Account, t)} />
      {currentAccount?.isSystem && !isLoading && quizzes?.count === 0 && (
        <FlexContainer
          align="center"
          justify="space-between"
          gap={16}
          className="mb-4"
        >
          <h1 className="mb-0">{t('security.culture.survey')}</h1>
          {currentAccount?.isSystem && (
            <AppButton
              id="create-survey-button"
              label={t('security.culture.new.survey')}
              onClick={() => setSurveyModal(true)}
            />
          )}
        </FlexContainer>
      )}
      {isLoading ? (
        <FlexContainer direction="column" height="100%" className="mt-5">
          <ProgressSpinner />
          <h3>{t('content.details.loading')}</h3>
        </FlexContainer>
      ) : (
        <StyledContainer
          id="surveys-container"
          direction="column"
          flex="1"
          align="flex-start"
          justify="flex-start"
          gap={16}
        >
          {quizzes?.result
            .sort((a: Quiz, b: Quiz) =>
              String(a.account || '').localeCompare(String(b.account || '')),
            )
            .map((quiz: Quiz) => (
              <Survey
                isMasterSurvey={!quiz.account}
                isSystemAccount={!!currentAccount?.isSystem}
                key={quiz.id}
                icon={quiz.account ? <SCEditIcon /> : <SurveyTemplateIcon />}
                title={quiz.title}
                onCopy={() => handleCopyAndEditSurvey(quiz.id as string)}
                canCopySurveys={quizzes.count < 2}
                onPreview={() => setPreviewSurveyId(quiz.id as string)}
                onEdit={() => {
                  setCurrentSurvey(quiz);
                  setSurveyModal(true);
                }}
                onDelete={() => handleDeleteSurvey(quiz.id as string)}
              />
            ))}
        </StyledContainer>
      )}

      <SurveyModal
        visible={surveyModal}
        onHide={() => {
          setSurveyModal(false);
          setCurrentSurvey(undefined);
        }}
        surveyId={currentSurvey?.id}
        refetch={() => refetch()}
      />
      <SurveyPreviewModal
        visible={!!previewSurveyId}
        onHide={() => setPreviewSurveyId(undefined)}
        surveyId={previewSurveyId}
      />
    </FlexContainer>
  );
};
